import { Image } from 'expo-image';
import { useState } from 'react';
import { View } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import { useAdmin } from '@/api/partners/admins/use-admin';
import { useRequestEonOneEarlyAccess } from '@/api/partners/admins/use-request-eon-one-early-access';
import mixpanelService from '@/core/clients/mixpanel';
import { showErrorMessage, Text } from '@/ui';
import { Icon } from '@/ui/icon';
import { Checkmark } from '@/ui/icons/checkmark';
import { DesktopMacbook } from '@/ui/icons/desktop-macbook';
import { Dollar } from '@/ui/icons/dollar';
import { Integrations } from '@/ui/icons/integrations';

import { EonOneButton } from './button';
import { FeatureCarousel } from './feature-carousel';

const darkImgSrc = require('../../../public/images/partners/eon-one/eonone-logo-dark.png');
const lightImgSrc = require('../../../public/images/partners/eon-one/eonone-logo-light.png');

const SLIDES = [
  {
    title: "Booking Site That's 100% Yours",
    body: 'A fully white-labeled portal using your customized subdomain, branding (logo and colors).',
    icon: DesktopMacbook,
    iconSize: 26,
    image: require('../../../public/images/partners/eon-one/coming-soon/slide-1.png'),
  },
  {
    title: 'Get Paid More When Customers Book Direct',
    body: 'Own your customers and keep a higher rate when they book with you direct',
    icon: Dollar,
    iconSize: 20,
    image: require('../../../public/images/partners/eon-one/coming-soon/slide-2.png'),
  },
  {
    title: 'Enterprise Grade Integrations',
    body: 'Bookings, cancellations, and payouts land in the channel your team already watches.',
    icon: Integrations,
    iconSize: 26,
    image: require('../../../public/images/partners/eon-one/coming-soon/slide-3.png'),
  },
] as const;

export default function JoinEonOne() {
  const { styles, theme } = useStyles(stylesheet);
  const [activeIndex, setActiveIndex] = useState(0);

  const { data: admin } = useAdmin();
  const { mutateAsync: requestEarlyAccess, isPending: isRequesting } =
    useRequestEonOneEarlyAccess();
  const hasRequested = !!admin?.eonOneEarlyAccessRequestedAt;

  const handleRequestAccess = async () => {
    mixpanelService.track('Button Clicked', {
      'Button Name': 'Request Early Access',
    });

    try {
      await requestEarlyAccess();
    } catch {
      showErrorMessage('Unable to request access. Please try again.');
    }
  };

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <View style={styles.header}>
        <Image
          style={styles.logo}
          source={
            UnistylesRuntime.themeName === 'dark' ? darkImgSrc : lightImgSrc
          }
          contentFit="contain"
          contentPosition="left center"
        />

        <Text type="largeTitle">Built For your brand. Backed by Eon.</Text>

        {hasRequested ? (
          <View style={styles.requested}>
            <View style={styles.requestedRow}>
              <Icon
                type="naked"
                icon={<Checkmark />}
                width={16}
                height={16}
                fillColor={theme.colors.success}
              />
              <Text type="bodyBold" style={{ color: theme.colors.success }}>
                You’re on the list!
              </Text>
            </View>
            <Text
              type="caption1"
              style={{ color: theme.colors.text.secondary }}
            >
              We will contact you via email.
            </Text>
          </View>
        ) : (
          <EonOneButton
            text="Request Early Access"
            onPress={handleRequestAccess}
            loading={isRequesting}
            disabled={isRequesting}
          />
        )}
      </View>

      <View style={styles.body}>
        <FeatureCarousel features={SLIDES} onIndexChange={setActiveIndex} />

        <View style={styles.dots}>
          {SLIDES.map((slide, index) => (
            <View
              key={slide.title}
              style={[
                styles.dot,
                activeIndex === index ? styles.dotActive : styles.dotInactive,
              ]}
            />
          ))}
        </View>
      </View>
    </ScrollView>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background_1,
  },
  content: {
    flexGrow: 1,
    paddingTop: 64,
    paddingBottom: 24,
    paddingHorizontal: 64,
    gap: 32,
  },
  header: {
    gap: 24,
    alignItems: 'flex-start',
  },
  logo: {
    width: 94,
    height: 24,
  },
  requested: {
    gap: 6,
  },
  requestedRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  body: {
    flex: 1,
    gap: 16,
    minHeight: 0,
  },
  dots: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 8,
    marginTop: 48,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  dotActive: {
    backgroundColor: theme.colors.text.primary,
  },
  dotInactive: {
    backgroundColor: theme.colors.text.secondary,
    opacity: 0.4,
  },
}));
